import { CustomData, IframeDialogClosed, OpenIframeDialog } from "@components/utils/IframeDialog/IframeDialog";
import toast from "@components/utils/Toaster/toast";
import { WSWithdrawalCancelledEvent } from "@constants/ws";
import { isGameIframeDialogOpen } from "@utils/ui";
import { useCallback, useEffect, useRef } from "react";
import reactHotToast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { useWSWithdrawalCancelled } from "./useAppWS";
import useContactLinks from "./useContactLinks";

interface PendingNotification {
  baseMessage: string;
  reasonLabel?: string;
  reasonValue?: string;
  actionButtonText: string;
  onActionClick: () => void;
  toastId: string;
}

const MAX_PENDING_NOTIFICATIONS = 10;
const MAX_ACTIVE_TOAST_IDS = 50;

export default function useWithdrawalCancelledNotification() {
  const { t } = useTranslation();
  const { openContact } = useContactLinks();
  const pendingNotificationsRef = useRef<PendingNotification[]>([]);
  const activeToastIdsRef = useRef<Set<string>>(new Set());

  const showNotification = useCallback((notification: PendingNotification) => {
    toast.warningWithAction(notification.baseMessage, notification.actionButtonText, notification.onActionClick, {
      id: notification.toastId,
      reasonLabel: notification.reasonLabel,
      reasonValue: notification.reasonValue,
    });
    activeToastIdsRef.current.add(notification.toastId);

    // Limit the size of activeToastIdsRef to prevent memory leaks
    if (activeToastIdsRef.current.size > MAX_ACTIVE_TOAST_IDS) {
      const idsArray = Array.from(activeToastIdsRef.current);
      // Remove the oldest half of the IDs
      const idsToRemove = idsArray.slice(0, Math.floor(idsArray.length / 2));
      idsToRemove.forEach((id) => {
        activeToastIdsRef.current.delete(id);
        reactHotToast.dismiss(id);
      });
    }
  }, []);

  useEffect(() => {
    const activeToastIdsRefValue = activeToastIdsRef.current;
    const pendingNotificationsRefValue = pendingNotificationsRef.current;

    const handleIframeOpen = (e: Event) => {
      const customEvent = e as CustomEvent<CustomData>;
      const isGameIframe = customEvent.detail.options?.isGame === true;

      if (isGameIframe) {
        // Dismiss all currently shown withdrawal cancel toasts
        activeToastIdsRefValue.forEach((toastId) => {
          reactHotToast.dismiss(toastId);
        });
        activeToastIdsRefValue.clear();
        // Clear any pending notifications
        pendingNotificationsRef.current.length = 0;
      }
    };

    const handleIframeClose = () => {
      // Show all pending notifications
      pendingNotificationsRefValue.forEach((notification) => {
        showNotification(notification);
      });
      pendingNotificationsRef.current.length = 0;
    };

    window.addEventListener(OpenIframeDialog, handleIframeOpen);
    window.addEventListener(IframeDialogClosed, handleIframeClose);

    // Cleanup on unmount
    return () => {
      window.removeEventListener(OpenIframeDialog, handleIframeOpen);
      window.removeEventListener(IframeDialogClosed, handleIframeClose);
      // Dismiss all active toasts
      activeToastIdsRefValue.forEach((toastId) => {
        reactHotToast.dismiss(toastId);
      });
      // Clear refs
      activeToastIdsRefValue.clear();
      pendingNotificationsRef.current.length = 0;
    };
  }, [showNotification]);

  const handleWithdrawalCancelled = useCallback(
    (event: WSWithdrawalCancelledEvent) => {
      const fundingTx = event.detail.fundingTx;
      const reasonValue = fundingTx?.memo || "";

      const baseMessage = t("your withdrawal has failed");
      const reasonLabel = reasonValue ? t("reason") : undefined;
      const actionButtonText = t("contact cs");

      const handleContactCsClick = () => {
        openContact();
      };

      const toastId = `withdrawal-cancelled-toast-${fundingTx?.orderRef || Date.now()}`;

      const notification: PendingNotification = {
        baseMessage,
        reasonLabel,
        reasonValue,
        actionButtonText,
        onActionClick: handleContactCsClick,
        toastId,
      };

      if (isGameIframeDialogOpen()) {
        // Limit the size of pending notifications to prevent memory leaks
        pendingNotificationsRef.current.push(notification);
        if (pendingNotificationsRef.current.length > MAX_PENDING_NOTIFICATIONS) {
          // Remove the oldest notifications, keeping only the most recent ones
          pendingNotificationsRef.current = pendingNotificationsRef.current.slice(-MAX_PENDING_NOTIFICATIONS);
        }
      } else {
        showNotification(notification);
      }
    },
    [t, openContact, showNotification],
  );

  useWSWithdrawalCancelled({ cb: handleWithdrawalCancelled });
}
